import pino from 'pino';

import type { LoggerOptions } from './types.js';

/**
 * pino applies its Error serializer to the `err` key only. The convention across
 * this repo is `logger.error({ error }, '...')`, which meant Error fields — all
 * non-enumerable — were dropped and the log recorded `"error":{}`, losing the
 * message and stack precisely when they were needed. Map both keys.
 *
 * `stdSerializers.err` returns non-Error values unchanged, so the many call
 * sites that pass a plain string or object under `error` keep their shape.
 */
const ERROR_SERIALIZERS: NonNullable<pino.LoggerOptions['serializers']> = {
  err: pino.stdSerializers.err,
  error: pino.stdSerializers.err,
};

const PRETTY_TRANSPORT: pino.TransportSingleOptions = {
  target: 'pino-pretty',
  options: {
    colorize: true,
    translateTime: 'HH:MM:ss.l',
    ignore: 'pid,hostname',
  },
};

/**
 * Creates a pino logger configured for the given environment.
 *
 * - In development: pretty-printed output (unless a browser config is provided).
 * - In production: structured JSON (or browser console, or file destination).
 *
 * @param options - Environment-specific configuration
 * @returns A configured pino.Logger instance
 */
export function createLogger(options: LoggerOptions): pino.Logger {
  const {
    processLabel,
    isDev,
    level,
    base: extraBase,
    browser,
    destination,
  } = options;

  const resolvedLevel = level ?? (isDev ? 'debug' : 'info');

  const baseFields: Record<string, unknown> = {
    process: processLabel,
    ...extraBase,
  };

  if (browser) {
    // Serializers are intentionally omitted: pino's browser mode only applies
    // them with `browser.serialize`, and the renderer logs through console.*
    // where DevTools already renders Error objects usefully.
    return pino({
      level: resolvedLevel,
      browser,
      base: baseFields,
    });
  }

  if (isDev) {
    return pino(
      {
        level: resolvedLevel,
        transport: PRETTY_TRANSPORT,
        base: baseFields,
        serializers: ERROR_SERIALIZERS,
      },
    );
  }

  const loggerOptions: pino.LoggerOptions = {
    level: resolvedLevel,
    base: baseFields,
    serializers: ERROR_SERIALIZERS,
  };

  if (destination) {
    return pino(loggerOptions, destination);
  }

  return pino(loggerOptions);
}
